import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axiosInstance';
import DashboardLayout from '../dashboard/DashboardLayout';
import { 
  Search, 
  Mail, 
  Building2, 
  MapPin, 
  Users, 
  ExternalLink, 
  Copy, 
  Check, 
  Loader, 
  Database, 
  Briefcase, 
  Calendar, 
  AlertCircle, 
  X, 
  RefreshCw, 
  CheckCircle2,
  Zap,
  Clock,
  ChevronRight,
  TrendingUp,
  Globe
} from 'lucide-react';
import { trackPageView, trackButtonClick } from '../../utils/analytics';

const FindHRPage = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [searchType, setSearchType] = useState('auto');
  const [jobTitle, setJobTitle] = useState('');
  const [jobPost, setJobPost] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [storedResults, setStoredResults] = useState([]);
  const [storedQuery, setStoredQuery] = useState('');
  const [storedRole, setStoredRole] = useState('');
  const [loadingStored, setLoadingStored] = useState(false);
  const [error, setError] = useState('');
  const [errorType, setErrorType] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [copiedEmail, setCopiedEmail] = useState('');
  const [activeTab, setActiveTab] = useState('search');
  const [jobPostings, setJobPostings] = useState([]);
  const [loadingJobPostings, setLoadingJobPostings] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [retryCount, setRetryCount] = useState(0);
  const [usage, setUsage] = useState({ current: 0, limit: 100 });
  const [userTier, setUserTier] = useState('starter');

  useEffect(() => {
    trackPageView("/find-hr", "Find HR Contacts");
    loadStoredResults();
    fetchUsage();
  }, []);

  const fetchUsage = async () => {
    try {
      const response = await axiosInstance.get('/subscription/status');
      const u = response.data.usage || {};
      const l = response.data.tier?.limits || {};
      setUsage({
        current: u.hrSearches || 0,
        limit: l.hrSearches || 100
      });
      setUserTier(response.data.tier?.id || 'starter');
    } catch (err) {
      console.error('Failed to fetch usage:', err);
    }
  };

  const loadStoredResults = async () => {
    try {
      const response = await axiosInstance.get('/apollo/search');
      setStoredResults(response.data.results || []);
    } catch (err) {
      console.error('Failed to load stored results:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!query.trim()) {
      errors.query = 'Company name or URL required';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSearch = async (isRetry = false) => {
    setError(''); setErrorType(''); setSuccessMessage(''); setValidationErrors({});
    if (!validateForm()) return;
    trackButtonClick("Search", "Find HR Contacts");
    setLoading(true); setResults(null); 
    try {
      const response = await axiosInstance.post('/apollo/search-hr', {
        query: query.trim(), search_type: searchType, job_title: jobTitle.trim() || null, job_post: jobPost.trim() || null
      });
      setResults(response.data);
      if (response.data.hr_contacts?.length > 0) {
        setSuccessMessage(`Located ${response.data.hr_contacts.length} strategic targets`);
        setTimeout(() => setSuccessMessage(''), 5000);
      }
      loadStoredResults();
    } catch (err) {
      setError(err.response?.data?.detail || 'Search failed. Try again.');
      setErrorType('api');
    } finally { 
      setLoading(false); 
      fetchUsage();
    }
  };

  const handleSearchStored = async () => {
    setLoadingStored(true); setError('');
    try {
      const params = new URLSearchParams();
      if (storedQuery.trim()) params.append('query', storedQuery.trim());
      if (storedRole.trim()) params.append('role', storedRole.trim());
      const response = await axiosInstance.get(`/apollo/search?${params.toString()}`);
      setStoredResults(response.data.results || []);
    } catch (err) {
      setError('Failed to query local database');
    } finally { setLoadingStored(false); }
  };

  const handleCopyEmail = (email) => {
    navigator.clipboard.writeText(email);
    setCopiedEmail(email);
    setTimeout(() => setCopiedEmail(''), 2000);
  };

  const handleUseEmail = (email) => {
    navigate('/email', { state: { recipient: email } });
  };

  return (
    <DashboardLayout activeTab="find-hr" setTab={(t) => t === 'find-hr' ? null : navigate('/dashboard', { state: { tab: t } })}>
      <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-10 font-sans">
        
        {/* Modern Nav Tabs */}
        <div className="flex bg-white p-0.5 md:p-1 rounded-xl md:rounded-2xl border border-slate-200 shadow-sm w-full md:w-fit overflow-x-auto scrollbar-hide">
           {[
             { id: 'search', label: 'HR Prospector', icon: Search },
             { id: 'stored', label: 'Saved Contacts', icon: Database },
             { id: 'job-postings', label: 'Live Intel', icon: Briefcase }
           ].map(t => (
             <button
               key={t.id}
               onClick={() => { setActiveTab(t.id); if (t.id === 'stored') loadStoredResults(); }}
               className={`flex items-center gap-1.5 md:gap-2 px-3 md:px-4 lg:px-6 py-1.5 md:py-2 lg:py-2.5 rounded-lg md:rounded-xl text-[10px] md:text-xs lg:text-sm font-black transition-all whitespace-nowrap shrink-0 ${
                 activeTab === t.id 
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-100' 
                  : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
               }`}
             >
               <t.icon size={14} className="md:w-4 md:h-4 lg:w-[18px] lg:h-[18px]" />
               {t.label}
             </button>
           ))}
        </div>

        {error && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/20 backdrop-blur-sm animate-in fade-in duration-200" onClick={() => setError('')}>
             <div className="bg-white p-8 rounded-[2.5rem] shadow-2xl max-w-sm w-full mx-4 border-4 border-white ring-4 ring-red-50 animate-in zoom-in-95 duration-200 relative" onClick={e => e.stopPropagation()}>
                <button onClick={() => setError('')} className="absolute top-6 right-6 text-slate-300 hover:text-slate-600 transition-colors">
                   <X size={20} />
                </button>
                <div className="flex flex-col items-center text-center gap-5">
                   <div className="w-16 h-16 bg-red-50 text-red-500 rounded-3xl flex items-center justify-center rotate-3 shadow-inner">
                      <AlertCircle size={32} />
                   </div>
                   <div>
                      <h4 className="text-xl font-black text-slate-900 mb-2 italic tracking-tight">Search Interrupted</h4>
                      <p className="text-sm font-bold text-slate-400 leading-relaxed px-2">
                        {error}
                      </p>
                   </div>
                   <button onClick={() => setError('')} className="w-full py-3.5 bg-slate-900 text-white rounded-2xl text-sm font-black uppercase tracking-widest hover:bg-black transition-all shadow-xl shadow-slate-200 active:scale-95 italic">
                      Dismiss Notification
                   </button>
                </div>
             </div>
          </div>
        )}

        {successMessage && (
          <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-2xl flex items-center gap-3 text-emerald-700 font-bold animate-in slide-in-from-top-2">
             <CheckCircle2 size={18} /> <span className="text-sm">{successMessage}</span>
          </div>
        )}

        {activeTab === 'search' && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-12">
               <div className="bg-white border border-slate-200 rounded-[3rem] p-10 shadow-sm relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-12 opacity-5 scale-150 rotate-12 pointer-events-none"><Search size={180} /></div>
                  
                  <div className="max-w-4xl">
                     <h3 className="text-2xl font-black text-slate-900 tracking-tight italic flex items-center gap-3 mb-2">
                        <TrendingUp size={28} className="text-blue-500" /> Find Recruiter
                     </h3>
                     <p className="text-slate-400 text-sm font-medium mb-10 max-w-xl italic uppercase tracking-widest text-[10px]">Using Data to locate hiring decision makers</p>
                     
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="space-y-6">
                           <div className="space-y-2">
                              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Company Identification</label>
                              <div className="relative group">
                                 <input 
                                     type="text" 
                                     className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-3xl text-sm font-bold outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-500 transition-all border-b-4 shadow-inner"
                                     placeholder="Google, stripe.com, or LinkedIn URL" 
                                     value={query} 
                                     onChange={(e) => setQuery(e.target.value)} 
                                 />
                                 <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors" size={20} />
                              </div>
                           </div>
                           
                           <div className="space-y-2">
                              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Search Specificity</label>
                              <select className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-3xl text-sm font-bold outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-500 transition-all border-b-4 shadow-inner appearance-none cursor-pointer" value={searchType} onChange={(e) => setSearchType(e.target.value)}>
                                 <option value="auto">Auto-Detect</option>
                                <option value="company_name">Company Name</option>
                                 <option value="domain">Network Node (Website)</option>
                                 <option value="linkedin_url">Direct Identity (LinkedIn)</option>
                              </select>
                           </div>
                        </div>

                        <div className="space-y-6">
                           <div className="space-y-2">
                              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Target Persona / Role</label>
                              <div className="relative group">
                                 <input 
                                     type="text" 
                                     className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-3xl text-sm font-bold outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-500 transition-all border-b-4 shadow-inner"
                                     placeholder="e.g., Talent Acquisition, CTO" 
                                     value={jobTitle} 
                                     onChange={(e) => setJobTitle(e.target.value)} 
                                 />
                                 <Users className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors" size={20} />
                              </div>
                           </div>

                           <div className="space-y-2">
                              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Intelligence Feed (Job URL)</label>
                              <div className="relative group">
                                 <textarea 
                                     className="w-full pl-4 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-3xl text-sm font-bold outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-500 transition-all border-b-4 shadow-inner min-h-[50px] resize-none"
                                     placeholder="Paste job link for context..." 
                                     value={jobPost} 
                                     onChange={(e) => setJobPost(e.target.value)} 
                                 />
                              </div>
                           </div>
                        </div>
                     </div>

                      <div className="mt-10 flex flex-col md:flex-row items-center gap-6">
                        <button 
                           onClick={handleSearch}
                           disabled={loading || !query.trim() || (usage.limit !== -1 && usage.current >= usage.limit)}
                           className="px-12 py-4 bg-blue-600 text-white rounded-3xl text-base font-black hover:bg-blue-700 disabled:opacity-50 shadow-2xl shadow-blue-100 transition-all active:scale-95 flex items-center gap-3 italic w-full md:w-auto"
                        >
                           {loading ? <Loader size={20} className="animate-spin" /> : <Zap size={20} className="fill-white" />}
                           {loading ? 'Analyzing...' : 'Execute Prospecting'}
                        </button>

                        <div className="flex flex-col gap-1 min-w-[200px]">
                            <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest italic text-slate-400 px-1">
                                <span>Monthly Usage</span>
                                <span>{usage.limit === -1 ? 'Unlimited' : `${usage.current} / ${usage.limit}`}</span>
                            </div>
                            <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden border border-slate-200/50">
                                <div 
                                    className={`h-full transition-all duration-1000 ${usage.current >= usage.limit && usage.limit !== -1 ? 'bg-rose-500' : 'bg-blue-600'}`}
                                    style={{ width: usage.limit === -1 ? '100%' : `${Math.min((usage.current / usage.limit) * 100, 100)}%` }}
                                />
                            </div>
                            {usage.limit !== -1 && usage.current >= usage.limit && (
                                <span className="text-[9px] font-black text-rose-500 uppercase tracking-tighter mt-1 animate-pulse">Limit Reached. Upgrade required.</span>
                            )}
                        </div>
                     </div>
                  </div>
               </div>
            </div>

            {results && results.company && (
               <div className="lg:col-span-12 animate-in zoom-in-95 duration-500">
                  <div className="bg-slate-900 border border-slate-800 rounded-[3rem] p-10 text-white shadow-2xl space-y-10">
                     <div className="flex flex-col md:flex-row items-center justify-between gap-8 pb-10 border-b border-slate-800">
                        <div className="flex items-center gap-6">
                           <div className="w-20 h-20 bg-blue-600 rounded-[2rem] flex items-center justify-center text-3xl font-black italic shadow-2xl shadow-blue-900/40">
                              {results.company.name.slice(0, 1)}
                           </div>
                           <div>
                              <h4 className="text-3xl font-black tracking-tight italic">{results.company.name}</h4>
                              <div className="flex flex-wrap gap-4 mt-2">
                                 {results.company.domain && <a href={`https://${results.company.domain}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs font-bold text-blue-400 hover:text-blue-300 transition-colors uppercase tracking-widest"><Globe size={14}/> {results.company.domain}</a>}
                                 {results.company.location && <span className="flex items-center gap-1.5 text-xs font-bold text-slate-400 uppercase tracking-widest"><MapPin size={14}/> {results.company.location}</span>}
                              </div>
                           </div>
                        </div>
                        <div className="flex gap-4">
                           <div className="px-6 py-2 bg-slate-800 rounded-2xl border border-slate-700 text-right">
                              <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Industry Cluster</div>
                              <div className="text-sm font-black text-blue-400 italic mt-0.5">{results.company.industry || 'Tech / SaaS'}</div>
                           </div>
                        </div>
                     </div>

                     {results.hr_contacts?.length > 0 && (
                        <div className="space-y-6">
                           <h5 className="text-[11px] font-black text-slate-500 uppercase tracking-[0.2em] italic flex items-center gap-3">
                              <Users size={16} className="text-blue-500" /> Contacts Found
                           </h5>
                           <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                              {results.hr_contacts.map((contact, idx) => (
                                 <div key={idx} className="bg-white rounded-[2.5rem] p-8 shadow-sm group hover:scale-[1.02] transition-all hover:shadow-xl hover:shadow-blue-500/5 cursor-default relative overflow-hidden border border-slate-100">
                                    <div className="absolute top-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity"><VerifiedIcon className="text-emerald-500"/></div>
                                    <div className="flex items-center gap-4 mb-6">
                                       <div className="w-12 h-12 bg-slate-100 rounded-2xl flex items-center justify-center text-slate-400 group-hover:bg-blue-600 group-hover:text-white transition-all font-black italic shadow-inner">
                                          {String(contact.name || '?').slice(0, 1)}
                                       </div>
                                       <div>
                                          <div className="text-base font-black text-slate-900 leading-none">{contact.name || 'Anonymous'}</div>
                                          <div className="text-[10px] font-black text-blue-500 uppercase tracking-widest mt-1.5 italic h-4 overflow-hidden line-clamp-1">{contact.title || 'Decision Maker'}</div>
                                       </div>
                                    </div>
                                    
                                    <div className="space-y-3">
                                       {contact.email && (
                                          <div className="flex items-center justify-between p-3.5 bg-slate-50 border border-slate-100 rounded-2xl group/btn hover:bg-white hover:border-blue-200 transition-all">
                                             <div className="text-[11px] font-bold text-slate-600 truncate mr-4">{contact.email}</div>
                                             <div className="flex gap-2">
                                                <button onClick={() => handleCopyEmail(contact.email)} className="p-2 bg-white text-slate-400 hover:text-blue-600 rounded-xl shadow-sm border border-slate-200 transition-all">{copiedEmail === contact.email ? <Check size={14} className="text-emerald-500"/> : <Copy size={14}/>}</button>
                                                <button onClick={() => handleUseEmail(contact.email)} className="p-2 bg-white text-slate-400 hover:text-blue-600 rounded-xl shadow-sm border border-slate-200 transition-all"><Mail size={14}/></button>
                                             </div>
                                          </div>
                                       )}
                                       {contact.linkedin_url && (
                                          <a href={contact.linkedin_url} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 w-full py-3 bg-slate-900 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-black transition-all shadow-lg active:scale-95">
                                             <ExternalLink size={14} /> Profile Intelligence
                                          </a>
                                       )}
                                    </div>
                                 </div>
                              ))}
                           </div>
                        </div>
                     )}
                  </div>
               </div>
            )}
          </div>
        )}

        {activeTab === 'stored' && (
          <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4">
             <div className="bg-white border border-slate-200 rounded-[3rem] p-10 shadow-sm">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                   <div className="md:col-span-1">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Archive Query</label>
                      <div className="relative group mt-2">
                         <input className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-bold outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-500 transition-all border-b-4 shadow-inner" placeholder="Search by name..." value={storedQuery} onChange={e => setStoredQuery(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleSearchStored()} />
                         <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors" size={16} />
                      </div>
                   </div>
                   <div className="md:col-span-1">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Persona Filter</label>
                      <div className="relative group mt-2">
                         <input className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-bold outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-500 transition-all border-b-4 shadow-inner" placeholder="Recruiter, Manager..." value={storedRole} onChange={e => setStoredRole(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleSearchStored()} />
                         <Users className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors" size={16} />
                      </div>
                   </div>
                   <div className="md:col-span-1 flex items-end">
                      <button className="w-full py-3 bg-slate-900 text-white rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-black transition-all shadow-xl shadow-slate-200 italic flex items-center justify-center gap-3" onClick={handleSearchStored} disabled={loadingStored}>
                         {loadingStored ? <Loader size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                         <span>{loadingStored ? 'Querying...' : 'Local Cache Search'}</span>
                      </button>
                   </div>
                </div>
             </div>

             <div className="space-y-8">
                {storedResults.map(company => (
                    <div key={company.id} className="bg-white border border-slate-200 rounded-[3rem] p-10 shadow-sm group hover:border-blue-200 transition-all">
                        <div className="flex flex-col md:flex-row items-center justify-between mb-8 pb-8 border-b border-slate-100 gap-6">
                            <div className="flex items-center gap-6">
                                <div className="w-16 h-16 bg-slate-100 rounded-3xl flex items-center justify-center text-2xl font-black text-slate-400 italic group-hover:bg-blue-600 group-hover:text-white transition-all shadow-inner uppercase">
                                    {company.name.slice(0, 1)}
                                </div>
                                <div>
                                    <h3 className="text-2xl font-black text-slate-900 tracking-tight italic">{company.name}</h3>
                                    <div className="flex gap-4 mt-1">
                                       {company.domain && <div className="text-[10px] font-bold text-blue-500 uppercase tracking-widest">{company.domain}</div>}
                                       <div className="text-[10px] font-black text-slate-300 uppercase tracking-widest">{new Date(company.updated_at).toLocaleDateString()}</div>
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-4">
                               <a href={`https://${company.domain}`} target="_blank" rel="noreferrer" className="p-2.5 bg-slate-50 text-slate-400 hover:text-blue-600 rounded-xl transition-all border border-slate-100"><Globe size={18}/></a>
                               <a href={company.linkedin_url} target="_blank" rel="noreferrer" className="p-2.5 bg-slate-50 text-slate-400 hover:text-blue-600 rounded-xl transition-all border border-slate-100"><ExternalLink size={18}/></a>
                            </div>
                        </div>

                         {company.hr_contacts?.length > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {company.hr_contacts.map((contact) => (
                                    <div key={contact.id} className="bg-slate-50 border border-slate-100 rounded-3xl p-6 group/card hover:bg-white hover:border-blue-100 hover:shadow-lg transition-all">
                                        <div className="flex items-center gap-3 mb-5">
                                           <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-slate-400 group-hover/card:bg-blue-600 group-hover/card:text-white transition-all font-black text-xs italic shadow-sm">{String(contact.name || '?').slice(0, 1)}</div>
                                           <div className="min-w-0">
                                              <div className="font-black text-slate-900 text-sm truncate">{contact.name || 'Anonymous'}</div>
                                              <div className="text-[9px] font-black text-blue-500 uppercase tracking-widest truncate italic">{contact.title || 'Decision Maker'}</div>
                                           </div>
                                        </div>
                                        {contact.email && (
                                           <div className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-xl shadow-inner group-hover/card:bg-slate-50 transition-colors">
                                              <div className="text-[10px] font-bold text-slate-500 truncate mr-2">{contact.email}</div>
                                              <div className="flex gap-1">
                                                 <button onClick={() => handleCopyEmail(contact.email)} className="p-1.5 hover:bg-white rounded transition-all text-slate-400 hover:text-blue-600">{copiedEmail === contact.email ? <Check size={12} className="text-emerald-600"/> : <Copy size={12}/>}</button>
                                                 <button onClick={() => handleUseEmail(contact.email)} className="p-1.5 hover:bg-white rounded transition-all text-slate-400 hover:text-blue-600"><Mail size={12}/></button>
                                              </div>
                                           </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                         )}
                    </div>
                ))}
             </div>
          </div>
        )}

        {/* Intelligence / Intel Tab */}
        {activeTab === 'job-postings' && (
           <div className="bg-white border border-slate-200 rounded-[3rem] p-20 text-center animate-in zoom-in-95">
              <div className="w-24 h-24 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-8 text-blue-500">
                 <Briefcase size={48} />
              </div>
              <h3 className="text-2xl font-black text-slate-900 tracking-tight italic uppercase">Premium Intelligence Feed</h3>
              <p className="text-slate-500 max-w-lg mx-auto mt-4 font-medium italic">Our live job posting intelligence engine is currently synchronizing with global databases. Real-time hiring signals will appear here shortly.</p>
              <button onClick={() => setActiveTab('search')} className="mt-10 px-10 py-3.5 bg-slate-900 text-white rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-black transition-all shadow-xl shadow-slate-200 italic">Back to Prospector</button>
           </div>
        )}
      </div>
    </DashboardLayout>
  );
};

const VerifiedIcon = ({ className }) => (
  <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M23 12l-2.44-2.79.34-3.69-3.61-.82-1.89-3.2L12 2.96 8.6 1.5 6.71 4.7 3.1 5.52l.34 3.7L1 12l2.44 2.79-.34 3.69 3.61.82 1.89 3.2L12 21.04l3.4 1.46 1.89-3.2 3.61-.82-.34-3.7L23 12zm-13 5l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z" />
  </svg>
);

export default FindHRPage;
